import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Property from "./Property";
import { getSandboxProperty, approveSandboxPreview, SandboxPropertyResult } from "../utils/properties";
import useThemeStore from "../store/themeStore";

function SandboxProperty() {
  const { token } = useParams<{ token: string }>();
  const [propertyData, setPropertyData] = useState<SandboxPropertyResult>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const setTheme = useThemeStore((state) => state.setTheme);
  const theme = useThemeStore((state) => state.theme);

  const [approved, setApproved] = useState(false);
  const [approvedDate, setApprovedDate] = useState<string>("");
  const [approvedIP, setApprovedIP] = useState<string>("");
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("No preview token provided.");
      setLoading(false);
      return;
    }

    const fetchSandboxProperty = async () => {
      try {
        const data = await getSandboxProperty(token);
        setPropertyData(data);

        // Check if already approved
        if ((data as any).previewAccepted) {
          setApproved(true);
          setApprovedDate(new Date((data as any).previewAccepted).toLocaleString("en-GB", {
            day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
          }));
        }

        // Apply the whitelabel theme from the sandbox response
        if (data.whitelabelTheme) {
          const themeToApply = {
            ...data.whitelabelTheme,
            logo: data.theme?.logo || data.whitelabelTheme.logo,
          };
          setTheme(themeToApply);
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError("This preview link has expired or been revoked. Please request a new one.");
        } else if (err.response?.status === 404) {
          setError("Property not found. It may have been removed.");
        } else {
          setError("Failed to load property preview. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSandboxProperty();
  }, [token, setTheme]);

  const handleApprove = async () => {
    if (!token || approving) return;
    setApproving(true);
    try {
      const result = await approveSandboxPreview(token);
      setApproved(true);
      setApprovedDate(new Date(result.previewAccepted).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      }));
      setApprovedIP(result.previewIP);
    } catch (err) {
      console.error("Error approving preview:", err);
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div
          className="animate-spin inline-block w-8 h-8 border-[2px] border-gray-800 border-t-transparent rounded-full"
          role="status"
          aria-label="loading"
        >
          <span className="sr-only">Loading preview...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">&#128274;</div>
          <h1 className="text-2xl font-bold mb-4">Preview Unavailable</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-400">
            If you believe this is an error, please contact the team who sent you this link.
          </p>
        </div>
      </div>
    );
  }

  const bannerTop = window.innerWidth <= 640 ? "92px" : "128px";
  const approveTop = window.innerWidth <= 640 ? "136px" : "172px";

  return (
    <>
      <Helmet>
        <title>Sandbox Preview</title>
      </Helmet>

      {/* Sandbox Banner — fixed under the header */}
      <div
        className="fixed left-0 right-0 z-[1001] text-center py-3 px-4 font-semibold text-sm shadow-md"
        style={{
          top: bannerTop,
          backgroundColor: theme?.color2 || "#f59e0b",
          color: "#ffffff",
        }}
      >
        Preview Mode for Upcoming Listing
      </div>

      {/* Approve Button — fixed under the banner */}
      <div
        className="fixed left-0 right-0 z-[1001] text-center py-2 px-4 shadow-sm"
        style={{
          top: approveTop,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {!approved ? (
          <button
            onClick={handleApprove}
            disabled={approving}
            className="font-semibold text-sm px-6 py-2 rounded-md transition-colors"
            style={{
              backgroundColor: theme?.color1 || "#1E5188",
              color: "#ffffff",
              opacity: approving ? 0.7 : 1,
              cursor: approving ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => { if (!approving) e.currentTarget.style.backgroundColor = theme?.color2 || "#f59e0b"; }}
            onMouseLeave={(e) => { if (!approving) e.currentTarget.style.backgroundColor = theme?.color1 || "#1E5188"; }}
          >
            {approving ? "Approving..." : "Click Here To Approve"}
          </button>
        ) : (
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 font-semibold text-sm px-3 py-1.5 rounded-full border border-green-200">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              Approved
            </span>
            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full border border-gray-200">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              {approvedDate}
            </span>
            {approvedIP && (
              <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full border border-gray-200">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" /></svg>
                {approvedIP}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Extra padding to account for fixed banner + approve bar */}
      <div style={{ paddingTop: window.innerWidth <= 640 ? "100px" : "100px" }}>
        <Property sandboxMode={true} sandboxData={propertyData} />
      </div>
    </>
  );
}

export default SandboxProperty;
